import { useState } from 'react';
import { HttpStatusCode } from 'axios';
import { UseFormSetValue } from 'react-hook-form';
import toast from 'react-hot-toast';
import useApi from '../../../../../../../utils/AxiosWrapper';
import { AssetFormData, AssetType, VinFields, VinResult } from './types';

/** Initial state — matches the legacy `useState({make:'',model:'',year:''})`. */
const INITIAL_VIN_FIELDS: VinFields = { make: '', model: '', year: '' };

/**
 * NHTSA vPIC DecodeVin `VariableId` → field. Copied verbatim from the legacy
 * CreateAsset VIN lookup so decoded values land in exactly the same fields.
 */
const VIN_VARIABLE = {
  ERROR_CODE: 143,
  YEAR: 29,
  MODEL: 28,
  MAKE: 26,
  CLASS: 25,
} as const;

/** NHTSA returns "0" in the error-code field when the VIN decoded cleanly. */
const VIN_OK_ERROR_CODE = '0';

const buildDecodeUrl = (vin: string) =>
  `https://vpic.nhtsa.dot.gov/api/vehicles/DecodeVin/${vin}?format=json`;

type UseVinLookupArgs = {
  assetType: AssetType;
  setValue: UseFormSetValue<AssetFormData>;
  setLoading: (loading: boolean) => void;
};

/**
 * Create-only VIN decode (edit never re-decodes — VIN/make/model/year are
 * immutable after creation). Owns `vinFields` and the external NHTSA fetch.
 * On a bad VIN it toasts; on a thrown error it clears the VIN input — this is
 * byte-for-byte the legacy behavior. See spec parity snapshot, "VIN lookup".
 */
export function useVinLookup({
  assetType,
  setValue,
  setLoading,
}: UseVinLookupArgs) {
  // External (NHTSA) call goes through `callApi`, NOT `callProtectedApi`.
  const { callApi } = useApi();
  const [vinFields, setVinFields] = useState<VinFields>(INITIAL_VIN_FIELDS);

  // Legacy reset cleared to `{}` (fields -> undefined), NOT empty strings, so an
  // un-decoded create submit sends undefined make/model and NaN year. Preserved.
  const resetVinFields = () => setVinFields({});

  const fetchAssetInfoByVIN = async (vin: string) => {
    setLoading(true);
    try {
      const response = await callApi(
        '',
        'get',
        {},
        'Error occurred while fetching asset information by VIN Number.',
        {},
        buildDecodeUrl(vin)
      );
      const data: VinResult[] = response?.data?.Results;
      const error: string | undefined = data?.find(
        (item) => item?.VariableId === VIN_VARIABLE.ERROR_CODE
      )?.Value;
      if (
        data &&
        data?.length > 0 &&
        response?.status === HttpStatusCode.Ok &&
        error &&
        error === VIN_OK_ERROR_CODE
      ) {
        const vehicleYear =
          data.find((item) => item?.VariableId === VIN_VARIABLE.YEAR)?.Value ??
          '';
        const vehicleModel =
          data.find((item) => item?.VariableId === VIN_VARIABLE.MODEL)?.Value ??
          '';
        const vehicleMake =
          data.find((item) => item?.VariableId === VIN_VARIABLE.MAKE)?.Value ??
          '';
        if (assetType === 'truck') {
          const vehicleClass =
            data.find((item) => item?.VariableId === VIN_VARIABLE.CLASS)
              ?.Value?.[6] ?? '';
          setVinFields({
            make: vehicleMake,
            model: vehicleModel,
            year: vehicleYear,
            class: Number(vehicleClass),
          });
        } else {
          setVinFields({
            make: vehicleMake,
            model: vehicleModel,
            year: vehicleYear,
          });
        }
      } else {
        toast.error('Invalid VIN Number. Please enter a valid VIN Number.');
      }
    } catch (e: any) {
      console.error(
        'Error occurred while fetching asset information by VIN Number: ',
        e?.message
      );
      setValue('vinNumber', '');
    } finally {
      setLoading(false);
    }
  };

  return { vinFields, setVinFields, resetVinFields, fetchAssetInfoByVIN };
}
